import { useEffect } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../store";
import { performLogout } from "../utils/authSession";

/**
 * Automatically logs the user out when the
 * absolute session expiration time is reached.
 */
export const useSessionTimeout = (): void => {
  const isAuthenticated = useSelector(
    (state: RootState) => state.auth.isAuthenticated,
  );

  const sessionExpiresAt = useSelector(
    (state: RootState) => state.auth.sessionExpiresAt,
  );

  useEffect(() => {
    if (!isAuthenticated || !sessionExpiresAt) {
      return;
    }

    let timeoutId: number | undefined;

    /**
     * Rechecks the real expiry time and
     * schedules logout for the remaining time.
     */
    const scheduleLogout = (): void => {
      if (timeoutId !== undefined) {
        window.clearTimeout(timeoutId);
      }

      const remainingTime = sessionExpiresAt - Date.now();

      if (remainingTime <= 0) {
        void performLogout();
        return;
      }

      timeoutId = window.setTimeout(() => {
        void performLogout();
      }, remainingTime);
    };

    /**
     * Browser timers may be paused or throttled
     * while a tab is hidden, so recheck when the
     * user returns to the tab.
     */
    const handleVisibilityChange = (): void => {
      if (document.visibilityState === "visible") {
        scheduleLogout();
      }
    };

    const handleWindowFocus = (): void => {
      scheduleLogout();
    };

    scheduleLogout();

    document.addEventListener("visibilitychange", handleVisibilityChange);

    window.addEventListener("focus", handleWindowFocus);

    return () => {
      if (timeoutId !== undefined) {
        window.clearTimeout(timeoutId);
      }

      document.removeEventListener("visibilitychange", handleVisibilityChange);

      window.removeEventListener("focus", handleWindowFocus);
    };
  }, [isAuthenticated, sessionExpiresAt]);
};
